/* =====================================================================
 * SlashCommandMenu — "/" 입력 시 표시되는 명령어 메뉴
 * ===================================================================== */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import type { Editor } from '@tiptap/react';
import { scheduleForceFixTables } from './tableFix';
import {
  Type,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  CheckSquare,
  Quote,
  Code,
  Minus,
  Image as ImageIcon,
  Table as TableIcon,
  Sparkles,
  PenLine,
  Languages,
  FileText,
  Search,
} from 'lucide-react';

/* ------------------------------------------------------------------ */
/* Command definitions                                                 */
/* ------------------------------------------------------------------ */

interface SlashItem {
  id: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  category: 'basic' | 'media' | 'ai';
  action: (editor: Editor) => void;
}

const SLASH_COMMANDS: SlashItem[] = [
  /* ── 기본 블록 ── */
  {
    id: 'paragraph',
    label: '텍스트',
    description: '일반 텍스트 블록',
    icon: <Type size={18} />,
    category: 'basic',
    action: (e) => e.chain().focus().setParagraph().run(),
  },
  {
    id: 'h1',
    label: '제목 1',
    description: '큰 제목',
    icon: <Heading1 size={18} />,
    category: 'basic',
    action: (e) => e.chain().focus().toggleHeading({ level: 1 }).run(),
  },
  {
    id: 'h2',
    label: '제목 2',
    description: '중간 제목',
    icon: <Heading2 size={18} />,
    category: 'basic',
    action: (e) => e.chain().focus().toggleHeading({ level: 2 }).run(),
  },
  {
    id: 'h3',
    label: '제목 3',
    description: '작은 제목',
    icon: <Heading3 size={18} />,
    category: 'basic',
    action: (e) => e.chain().focus().toggleHeading({ level: 3 }).run(),
  },
  {
    id: 'bullet-list',
    label: '글머리 기호 목록',
    description: '간단한 목록',
    icon: <List size={18} />,
    category: 'basic',
    action: (e) => e.chain().focus().toggleBulletList().run(),
  },
  {
    id: 'ordered-list',
    label: '번호 목록',
    description: '순서가 있는 목록',
    icon: <ListOrdered size={18} />,
    category: 'basic',
    action: (e) => e.chain().focus().toggleOrderedList().run(),
  },
  {
    id: 'task-list',
    label: '할 일 목록',
    description: '체크박스가 있는 목록',
    icon: <CheckSquare size={18} />,
    category: 'basic',
    action: (e) => e.chain().focus().toggleTaskList().run(),
  },
  {
    id: 'blockquote',
    label: '인용',
    description: '인용구 블록',
    icon: <Quote size={18} />,
    category: 'basic',
    action: (e) => e.chain().focus().toggleBlockquote().run(),
  },
  {
    id: 'code-block',
    label: '코드 블록',
    description: '코드를 작성할 수 있는 블록',
    icon: <Code size={18} />,
    category: 'basic',
    action: (e) => e.chain().focus().toggleCodeBlock().run(),
  },
  {
    id: 'divider',
    label: '구분선',
    description: '수평 구분선',
    icon: <Minus size={18} />,
    category: 'basic',
    action: (e) => e.chain().focus().setHorizontalRule().run(),
  },

  /* ── 미디어 ── */
  {
    id: 'image',
    label: '이미지',
    description: '이미지 삽입',
    icon: <ImageIcon size={18} />,
    category: 'media',
    action: (e) => {
      e.chain().focus().insertContent({ type: 'imageBlock' }).run();
    },
  },
  {
    id: 'table',
    label: '표',
    description: '3x3 표 삽입',
    icon: <TableIcon size={18} />,
    category: 'media',
    action: (e) => {
      if (import.meta.env.DEV) {
        const w = window as Window & { __TABLE_DEBUG_LOGS__?: Array<Record<string, unknown>> };
        const entry = {
          at: new Date().toISOString(),
          label: 'insert-table:SlashCommandMenu',
          payload: {
            selectionFrom: e.state.selection.from,
            selectionTo: e.state.selection.to,
          },
        };
        if (!w.__TABLE_DEBUG_LOGS__) w.__TABLE_DEBUG_LOGS__ = [];
        w.__TABLE_DEBUG_LOGS__.push(entry);
        // eslint-disable-next-line no-console
        console.log('[TableDebug] insert-table:SlashCommandMenu', entry.payload);
      }
      e.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
      scheduleForceFixTables(e);
    },
  },

  /* ── AI ── */
  {
    id: 'ai-write',
    label: 'AI로 글쓰기',
    description: 'AI에게 글 작성 요청',
    icon: <Sparkles size={18} />,
    category: 'ai',
    action: () => {
      // AI 커맨드 패널은 외부에서 처리
      document.dispatchEvent(new CustomEvent('doc:ai-command', { detail: { type: 'write' } }));
    },
  },
  {
    id: 'ai-continue',
    label: 'AI로 이어쓰기',
    description: '기존 내용 이어서 작성',
    icon: <PenLine size={18} />,
    category: 'ai',
    action: () => {
      document.dispatchEvent(new CustomEvent('doc:ai-command', { detail: { type: 'continue' } }));
    },
  },
  {
    id: 'ai-summarize',
    label: 'AI 요약',
    description: '문서 내용 요약',
    icon: <FileText size={18} />,
    category: 'ai',
    action: () => {
      document.dispatchEvent(new CustomEvent('doc:ai-command', { detail: { type: 'summarize' } }));
    },
  },
  {
    id: 'ai-translate',
    label: 'AI 번역',
    description: '다른 언어로 번역',
    icon: <Languages size={18} />,
    category: 'ai',
    action: () => {
      document.dispatchEvent(new CustomEvent('doc:ai-command', { detail: { type: 'translate' } }));
    },
  },
];

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

interface SlashCommandMenuProps {
  editor: Editor;
}

export const SlashCommandMenu: React.FC<SlashCommandMenuProps> = ({ editor }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const menuRef = useRef<HTMLDivElement>(null);
  const slashPosRef = useRef<number | null>(null);

  /* ── 필터된 명령어 ── */
  const filtered = SLASH_COMMANDS.filter(
    (cmd) =>
      cmd.label.toLowerCase().includes(query.toLowerCase()) ||
      cmd.description.toLowerCase().includes(query.toLowerCase()),
  );

  /* ── 에디터 트랜잭션 감지 ── */
  useEffect(() => {
    if (!editor) return;

    const handleTransaction = () => {
      const { state } = editor;
      const { from } = state.selection;
      const textBefore = state.doc.textBetween(
        Math.max(0, from - 50),
        from,
        '\n',
      );

      const slashMatch = textBefore.match(/\/([^\s/]*)$/);

      if (slashMatch) {
        const coords = editor.view.coordsAtPos(from);
        const editorRect = editor.view.dom.getBoundingClientRect();

        setPosition({
          top: coords.bottom - editorRect.top + 8,
          left: coords.left - editorRect.left,
        });
        setQuery(slashMatch[1]);
        setSelectedIndex(0);
        setIsOpen(true);
        slashPosRef.current = from - slashMatch[0].length;
      } else {
        setIsOpen(false);
        slashPosRef.current = null;
      }
    };

    editor.on('transaction', handleTransaction);
    return () => {
      editor.off('transaction', handleTransaction);
    };
  }, [editor]);

  /* ── 명령어 실행 ── */
  const executeCommand = useCallback(
    (cmd: SlashItem) => {
      if (slashPosRef.current !== null) {
        const { from } = editor.state.selection;
        editor
          .chain()
          .focus()
          .deleteRange({ from: slashPosRef.current, to: from })
          .run();
      }
      cmd.action(editor);
      setIsOpen(false);
    },
    [editor],
  );

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const normalized = raw.startsWith('/') ? raw.slice(1) : raw;
    setQuery(normalized);
    setSelectedIndex(0);
  }, []);

  /* ── 키보드 핸들링 ── */
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (filtered.length > 0) {
          setSelectedIndex((i) => (i + 1) % filtered.length);
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (filtered.length > 0) {
          setSelectedIndex((i) => (i - 1 + filtered.length) % filtered.length);
        }
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (filtered[selectedIndex]) {
          executeCommand(filtered[selectedIndex]);
        }
      } else if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen, filtered, selectedIndex, executeCommand]);

  /* ── 선택 항목 스크롤 ── */
  useEffect(() => {
    if (!menuRef.current) return;
    const activeEl = menuRef.current.querySelector('[data-active="true"]');
    activeEl?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  if (!isOpen) return null;

  /* ── 카테고리별 그룹 ── */
  const categories = [
    { key: 'basic' as const, label: '기본 블록' },
    { key: 'media' as const, label: '미디어' },
    { key: 'ai' as const, label: 'AI' },
  ];

  let globalIdx = -1;

  return (
    <MenuWrap
      ref={menuRef}
      data-slash-menu="true"
      style={{ top: position.top, left: position.left }}
    >
      {/* ── 검색 영역 ── */}
      <SearchArea>
        <SearchIconWrap>
          <Search size={14} />
        </SearchIconWrap>
        <SearchPrefix>/</SearchPrefix>
        <SearchInput
          value={query}
          onChange={handleSearchChange}
          placeholder="검색어를 입력하세요"
        />
      </SearchArea>

      {/* ── 명령어 목록 ── */}
      {filtered.length === 0 ? (
        <EmptyResult>검색 결과가 없습니다</EmptyResult>
      ) : (
        categories.map((cat) => {
          const items = filtered.filter((c) => c.category === cat.key);
          if (items.length === 0) return null;
          return (
            <div key={cat.key}>
              <CategoryLabel>{cat.label}</CategoryLabel>
              {items.map((cmd) => {
                globalIdx++;
                const idx = globalIdx;
                return (
                  <MenuItem
                    key={cmd.id}
                    data-active={idx === selectedIndex}
                    $active={idx === selectedIndex}
                    onClick={() => executeCommand(cmd)}
                    onMouseEnter={() => setSelectedIndex(idx)}
                  >
                    <MenuIcon $isAI={cmd.category === 'ai'}>{cmd.icon}</MenuIcon>
                    <MenuText>
                      <MenuLabel>{cmd.label}</MenuLabel>
                      <MenuDesc>{cmd.description}</MenuDesc>
                    </MenuText>
                  </MenuItem>
                );
              })}
            </div>
          );
        })
      )}
    </MenuWrap>
  );
};

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const MenuWrap = styled.div`
  position: absolute;
  z-index: 100;
  width: 280px;
  max-height: 400px;
  overflow-y: auto;
  background: ${({ theme }) => theme.colors.bgPrimary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 10px;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  padding: 6px;
`;

const SearchArea = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  margin: 2px 2px 4px;
  background: ${({ theme }) => theme.colors.bgSecondary};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 6px;
`;

const SearchIconWrap = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const SearchPrefix = styled.span`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const SearchInput = styled.input`
  width: 100%;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textPrimary};

  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

const EmptyResult = styled.div`
  padding: 16px 10px;
  text-align: center;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const CategoryLabel = styled.div`
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textMuted};
  padding: 8px 10px 4px;
  letter-spacing: 0.03em;
`;

const MenuItem = styled.div<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 6px;
  cursor: pointer;
  background: ${({ $active, theme }) =>
    $active
      ? theme.mode === 'dark'
        ? 'rgba(255,255,255,0.08)'
        : 'rgba(0,0,0,0.04)'
      : 'transparent'};
  transition: background 100ms;

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'};
  }
`;

const MenuIcon = styled.div<{ $isAI?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  flex-shrink: 0;
  background: ${({ $isAI, theme }) =>
    $isAI
      ? 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)'
      : theme.mode === 'dark'
        ? 'rgba(255,255,255,0.06)'
        : 'rgba(0,0,0,0.04)'};
  color: ${({ $isAI, theme }) => ($isAI ? '#fff' : theme.colors.textSecondary)};
`;

const MenuText = styled.div`
  min-width: 0;
`;

const MenuLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const MenuDesc = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
